import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { API } from "../lib/api";
import GlobalSearch from "../components/GlobalSearch";
import {
  LayoutDashboard, Package, ShoppingCart, FileBarChart, Users as UsersIcon,
  Building2, Activity, Wallet, LogOut, Pill, Truck, BookOpen, Settings as SettingsIcon,
  UserCircle, PanelLeftClose, PanelLeft, Menu, X, Undo2, ExternalLink, Library,
} from "lucide-react";

const ALL_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "branch_head", "cashier", "accountant"] },
  { to: "/sales", label: "Sales POS", icon: ShoppingCart, roles: ["admin", "branch_head", "cashier"] },
  { to: "/sales/history", label: "Sales History", icon: Undo2, roles: ["admin", "branch_head", "cashier", "accountant"] },
  { to: "/stock", label: "Stock", icon: Package, roles: ["admin", "branch_head", "cashier", "accountant"] },
  { to: "/purchases", label: "Purchases", icon: Truck, roles: ["admin", "branch_head", "cashier", "accountant"] },
  { to: "/vendors", label: "Vendors", icon: BookOpen, roles: ["admin", "branch_head", "cashier", "accountant"] },
  { to: "/customers", label: "Customers", icon: UserCircle, roles: ["admin", "branch_head", "cashier", "accountant"] },
  { to: "/reports", label: "Reports", icon: FileBarChart, roles: ["admin", "branch_head", "cashier", "accountant"] },
  { to: "/accounts", label: "Accounts", icon: Wallet, roles: ["admin", "branch_head", "accountant"] },
  { to: "/branches", label: "Branches", icon: Building2, roles: ["admin"] },
  { to: "/master-catalog", label: "Master Catalog", icon: Library, roles: ["admin"] },
  { to: "/users", label: "Users", icon: UsersIcon, roles: ["admin"] },
  { to: "/activity", label: "Activity Log", icon: Activity, roles: ["admin"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, roles: ["admin", "branch_head"] },
];

const LS_KEY = "medicore_sidebar_mode";
const POPOUT_KEY = "medicore_popout_nav";

export default function AppShell() {
  const { user, settings, logout } = useAuth();
  const nav = useNavigate();
  const items = ALL_NAV.filter((n) => n.roles.includes(user?.role));

  // mode: 'expanded' | 'collapsed' | 'hidden'
  const [mode, setMode] = useState(() => localStorage.getItem(LS_KEY) || "expanded");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [popOut, setPopOut] = useState(() => localStorage.getItem(POPOUT_KEY) === "1");
  const [branchLabel, setBranchLabel] = useState("");

  useEffect(() => {
    // Branch-pinned users should see THEIR branch's pharmacy_name in the
    // sidebar, not the platform-wide store name. GET /branches is server
    // -scoped so branch users only receive their own branch here.
    if (!user?.branch_id) { setBranchLabel(""); return; }
    API.get("/branches").then(({ data }) => {
      const own = (data || []).find((b) => b.id === user.branch_id);
      if (own) setBranchLabel(own.pharmacy_name || own.name || "");
    }).catch(() => {});
  }, [user?.branch_id]);

  useEffect(() => { localStorage.setItem(LS_KEY, mode); }, [mode]);
  useEffect(() => { localStorage.setItem(POPOUT_KEY, popOut ? "1" : "0"); }, [popOut]);

  const doLogout = async () => { await logout(); nav("/login"); };
  const storeName = branchLabel || settings?.name || "MedOne";

  const toggleDesktop = () => {
    setMode((m) => (m === "expanded" ? "collapsed" : m === "collapsed" ? "hidden" : "expanded"));
  };

  const isCollapsed = mode === "collapsed";
  const isHidden = mode === "hidden";

  // Sidebar inner content (reused for desktop + mobile drawer)
  const SidebarContent = ({ compact = false, onItemClick }) => (
    <>
      <div className={`h-16 flex items-center gap-2.5 border-b border-slate-200 ${compact ? "px-3 justify-center" : "px-5"}`}>
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-500 text-white flex items-center justify-center shrink-0 shadow-sm shadow-emerald-500/25">
          <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden="true">
            <path d="M24 12v24M12 24h24" stroke="white" strokeWidth="5" strokeLinecap="round" />
          </svg>
        </div>
        {!compact && (
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold text-[15px] tracking-tight leading-none truncate" data-testid="brand-name" title={storeName}>{storeName}</div>
            <div className="overline text-[9px] mt-1">Pharmacy MIS</div>
          </div>
        )}
      </div>
      <nav className={`flex-1 overflow-y-auto ${compact ? "p-2" : "p-3"} space-y-0.5`}>
        {items.map(({ to, label, icon: Icon }) => {
          const handlePopOut = (e) => {
            e.preventDefault();
            window.open(to, `medone-${to}`,
              "width=1400,height=900,resizable=yes,scrollbars=yes,status=yes,menubar=no,toolbar=no");
            onItemClick && onItemClick();
          };
          if (popOut) {
            return (
              <button
                key={to}
                type="button"
                onClick={handlePopOut}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                title={compact ? `${label} (opens in new window)` : "Opens in new window"}
                className={`side-item ${compact ? "justify-center !px-2" : ""}`}
              >
                <Icon className="w-4 h-4 shrink-0" strokeWidth={2} />
                {!compact && (
                  <span className="truncate flex items-center gap-1.5 flex-1">
                    {label}
                    <ExternalLink className="w-3 h-3 text-slate-400 shrink-0" />
                  </span>
                )}
              </button>
            );
          }
          return (
            <NavLink
              key={to}
              to={to}
              onClick={onItemClick}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              title={compact ? label : undefined}
              end={to === "/" || to === "/sales"}
              className={({ isActive }) =>
                `side-item ${isActive ? "active" : ""} ${compact ? "justify-center !px-2" : ""}`
              }
            >
              <Icon className="w-4 h-4 shrink-0" strokeWidth={2} />
              {!compact && <span className="truncate">{label}</span>}
            </NavLink>
          );
        })}
      </nav>
      <div className={`border-t border-slate-200 ${compact ? "p-2" : "p-3"}`}>
        {!compact && (
          <div className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-100 mb-2">
            <div className="text-[11px] text-slate-500 font-medium">Signed in as</div>
            <div className="text-sm font-semibold text-slate-900 truncate">{user?.name}</div>
            <div className="text-xs text-slate-500 truncate capitalize">{user?.role} · {user?.email}</div>
          </div>
        )}
        <button
          onClick={doLogout}
          data-testid="logout-button"
          title={compact ? "Sign out" : undefined}
          className={`w-full flex items-center gap-2 text-sm text-slate-600 hover:text-[#B91C1C] px-3 py-2 rounded-lg hover:bg-red-50 transition-colors ${compact ? "justify-center" : ""}`}
        >
          <LogOut className="w-4 h-4" /> {!compact && "Sign out"}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]" data-testid="app-shell">
      {/* Desktop Sidebar — sticky full-height, collapsible, hideable */}
      {!isHidden && (
        <aside
          data-testid="app-sidebar"
          data-mode={mode}
          className={`hidden md:flex shrink-0 bg-white border-r border-slate-200 flex-col sticky top-0 h-screen transition-[width] duration-200 ease-out ${isCollapsed ? "w-16" : "w-60"}`}
        >
          <SidebarContent compact={isCollapsed} />
        </aside>
      )}

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white border-r border-slate-200 flex flex-col animate-in slide-in-from-left">
            <SidebarContent onItemClick={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 px-4 md:px-6 bg-white border-b border-slate-200 flex items-center gap-3 sticky top-0 z-20">
          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100 text-slate-600"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Desktop sidebar toggle */}
          <button
            className="hidden md:inline-flex p-2 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"
            onClick={toggleDesktop}
            data-testid="sidebar-toggle"
            title={isHidden ? "Show sidebar" : isCollapsed ? "Hide sidebar" : "Collapse sidebar"}
            aria-label="Toggle sidebar"
          >
            {isHidden ? <PanelLeft className="w-5 h-5" /> : isCollapsed ? <X className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
          </button>

          <GlobalSearch />
          <div className="ml-auto flex items-center gap-3">
            <button
              onClick={() => setPopOut((p) => !p)}
              data-testid="popout-toggle"
              title={popOut ? "Pop-out menu ON — nav items open in a new window" : "Pop-out menu OFF — single window mode"}
              className={`hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${popOut ? "bg-[#002FA7] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            >
              <ExternalLink className="w-3.5 h-3.5" /> Pop-out {popOut ? "ON" : "OFF"}
            </button>
            <span className="hidden md:inline text-xs text-slate-500">
              {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </span>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#002FA7] to-[#10B981] text-white flex items-center justify-center text-sm font-semibold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
          </div>
        </header>
        <main className="flex-1 page-enter">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
